import { defineStore } from 'pinia';
import { Addition, LocalDepositTracking } from '@/types/api/orders.types';
import { Cart, CartItem, EditCartItemBody, UpdateCartDetailBody } from '@/types/api/carts.types';
import {
  addItemToCart,
  deleteCart,
  deleteItemFromCart,
  editItemInCart,
  getCartDetails,
  updateCartDetails,
} from '@/api/carts.service';
import { BOQ, BOQItem } from '@/types/api/sites.types';
import { SelectedItems } from '@/types/api/build-boq';
import { useToastStore } from '@/stores/utils/toast.store';
import { useBuildBoqStore } from '@/stores/build-boq.store';

import i18n from '@/plugins/vue-i18n';
import { analyticsService } from '@/plugins/post-hog';
import { selectedOrgId } from '@/common/utils/role.utils';
import { collectBoqLineItems } from '@/common/utils/boq.utils';

const { t } = i18n.global;

export const VAT_MULTIPLIER = 1.15;

export const useCartsStore = defineStore('carts', {
  state: () => ({
    cart: {} as Cart,
    cartItems: [] as CartItem[],
    cartAdditions: [] as Addition[],
    boqCartItemsGroupedByVendor: [] as LocalDepositTracking[],
    cartActiveTab: localStorage.getItem('cartActiveTab') ?? '#order',
  }),

  actions: {
    // Item operations
    async updateCartItem(boqId: string, itemId: string, data: EditCartItemBody) {
      const response = await editItemInCart(boqId, itemId, data);

      if (response.status === 200) {
        this.cart = response.data.data;
        this.cartItems = response.data.data.items;
        this.boqCartItemsGroupedByVendor = [];
        this._mapCartItemsGroupedByVendor();
      }
    },

    async removeItemFromCart(boqId: string, itemId: string) {
      const response = await deleteItemFromCart(boqId, itemId);

      if (response.status === 204) {
        await this.getCart(boqId);
      }
    },

    async addToCart(boqId: string, item: BOQItem, groupItem: boolean) {
      const data = [
        {
          lineItemPath: item.path,
          itemInGroup: groupItem,
        },
      ];

      const response = await addItemToCart(boqId, data);

      if (response.status === 200) {
        this.cartItems = [...this.cartItems, ...response.data.data.addedItems];
        this.boqCartItemsGroupedByVendor = [];
        this._mapCartItemsGroupedByVendor();

        analyticsService.capture('boq_cart_item_added', {
          org_id: selectedOrgId.value!,
          boq_id: boqId,
        });

        return response.data.data.errors;
      }
    },

    async addSelectedItemsToCart(boq: BOQ) {
      const toastStore = useToastStore();
      const buildBoqStore = useBuildBoqStore();
      const itemsToAdd = buildBoqStore.selectedItems;

      /**
       * Filter out the lines nothing can be ordered against. Both are already unselectable in the
       * BOQ, so this is the guard against a selection that outlived the state it was made in.
       *
       * Resolved against the bill in hand rather than the flags the selection carries: those were
       * copied when the line was ticked, and a BOQ read since then can have flagged a line the user
       * is still holding selected — which is the case this guard exists for, and the one the
       * snapshot cannot see. The snapshot is the fallback for a line the bill no longer has at all
       * (PLE-1266).
       *
       * Whole line or nothing, never field by field: a live line that does not say it is flagged is
       * answering the question, so reading its silence as "no answer" would hand the decision back
       * to the snapshot this is meant to distrust.
       */
      const linesById = new Map(collectBoqLineItems(boq).map((line) => [line.id, line]));

      const isOrderable = (item: SelectedItems) => {
        const line = linesById.get(item.id) ?? item;

        return !line.hasPendingVariation && !line.vendorItemUnavailable;
      };
      const validItems = itemsToAdd.filter(isOrderable);
      const skippedLines = itemsToAdd
        .filter((item) => !isOrderable(item))
        .map((item) => item.path.join('-'))
        .join(', ');

      // Context: the skipped lines are cleared from the selection rather than held, because their
      // checkboxes are disabled — a selection the user cannot untick would follow them around.
      if (validItems.length === 0) {
        buildBoqStore.clearSelectedItems();
        toastStore.showToast(
          'warn',
          t('carts.bulkAddToCart.nothingAddedToastTitle'),
          t('carts.bulkAddToCart.skippedToastBody', { lines: skippedLines }),
        );
        return;
      }

      // Map the valid selected items to the format required by the API
      const data = validItems.map((item: SelectedItems) => ({
        lineItemPath: item.path,
        itemInGroup: item.isGroupOrGroupItem,
      }));

      const response = await addItemToCart(boq.id, data);

      if (response.status === 200) {
        this.cartItems = [...this.cartItems, ...response.data.data.addedItems];
        this.boqCartItemsGroupedByVendor = [];
        this._mapCartItemsGroupedByVendor();

        buildBoqStore.clearSelectedItems();

        if (skippedLines) {
          toastStore.showToast(
            'warn',
            t('carts.bulkAddToCart.partlyAddedToastTitle'),
            t('carts.bulkAddToCart.skippedToastBody', { lines: skippedLines }),
          );
        } else {
          toastStore.showToast(
            'success',
            t('carts.bulkAddToCart.successToastTitle'),
            t('carts.bulkAddToCart.successToastBody'),
          );
        }
      } else {
        console.error(t('carts.bulkAddToCart.errorToastBody'));
        toastStore.showToast(
          'error',
          t('carts.bulkAddToCart.errorToastTitle'),
          t('carts.bulkAddToCart.errorToastBody'),
        );
      }
    },

    // Cart operations
    async getCart(boqId: string) {
      const response = await getCartDetails(boqId);

      if (response.status === 200) {
        this.cart = response.data.data;
        this.cartItems = response.data.data.items;
        this.boqCartItemsGroupedByVendor = [];
        this._mapCartItemsGroupedByVendor();
      }
    },

    async clearCart(boqId: string) {
      const response = await deleteCart(boqId);

      if (response.status === 204) {
        this.cart = {} as Cart;
        this.cartItems = [];
        this.boqCartItemsGroupedByVendor = [];
      }
    },

    async updateCart(boqId: string, data: UpdateCartDetailBody) {
      const response = await updateCartDetails(boqId, data);

      if (response.status === 200) {
        this.cart = response.data.data;
        this.cartItems = response.data.data.items;
      }
    },

    addCartAddition(vendorId: string, vendorName: string, type: 'deposit' | 'retention') {
      const newAddition: Addition = {
        id: `${type}-${vendorId}-${Date.now()}`,
        type,
        vendorId,
        vendorName,
        percent: 0,
        paymentType: 'deposit',
        dueDate: new Date().toISOString().split('T')[0] ?? '',
        assigneeId: '',
      };

      this.cartAdditions.push(newAddition);

      const vendor = this.boqCartItemsGroupedByVendor.find((v) => v.vendorId === vendorId);
      if (vendor) {
        if (!vendor.additions) {
          vendor.additions = [];
        }
        vendor.additions.push(newAddition);
      }
    },

    removeCartAddition(additionId: string) {
      this.cartAdditions = this.cartAdditions.filter((a) => a.id !== additionId);

      for (const vendor of this.boqCartItemsGroupedByVendor) {
        if (vendor.additions) {
          vendor.additions = vendor.additions.filter((a) => a.id !== additionId);
        }
      }
    },

    hasCartDepositAddition(vendorId: string): boolean {
      return this.cartAdditions.some((a) => a.vendorId === vendorId && a.type === 'deposit');
    },

    _mapCartItemsGroupedByVendor() {
      this.boqCartItemsGroupedByVendor = [];

      for (const item of this.cartItems) {
        const existingVendor = this.boqCartItemsGroupedByVendor.find(
          (vendor) => vendor.vendorId === item.vendorId,
        );

        const amountExclVat =
          item.variation && item.variation.total !== 0
            ? (item.variation.rate ?? 0) * (item.variation.desiredQuantity ?? 0)
            : (item.total ?? 0);

        const amountToPayVendor = amountExclVat * (item.vatItem ? VAT_MULTIPLIER : 1);

        if (existingVendor) {
          existingVendor.amountExclVat += amountExclVat;
          existingVendor.amountToPayVendor += amountToPayVendor;
        } else {
          this.boqCartItemsGroupedByVendor.push({
            vendorId: item.vendorId,
            vendorName: item.vendorName,
            amountExclVat,
            amountToPayVendor,
          });
        }
      }
    },

    setCartActiveTab(tab: string) {
      this.cartActiveTab = tab;
      localStorage.setItem('cartActiveTab', tab);
    },

    clearCartStore() {
      this.cart = {} as Cart;
      this.cartItems = [];
      this.cartAdditions = [];
      this.boqCartItemsGroupedByVendor = [];
      this.setCartActiveTab('#order');
    },
  },
  getters: {
    cartItemCount: (state): number => {
      return state.cartItems.length;
    },
  },
});
